import { FC, useEffect, useMemo } from 'react';
import {
	Autocomplete,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	TextField,
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import {
	createMonthlyFeeRequest,
	getFeeClassOptionsRequest,
	resetSaveMonthlyFeeSuccess,
	updateMonthlyFeeRequest,
} from 'redux/reducers/easFee/reducer';
import {
	getFeeClassOptions,
	getMonthlyFeeSaveLoading,
	getMonthlyFeeSaveSuccess,
} from 'redux/reducers/easFee/selectors';
import { TMonthlyFeeDialogProps } from './types';

const validationSchema = Yup.object({
	name: Yup.string().required('Name is required').max(255, 'Max 255 characters'),
	description: Yup.string().max(1000, 'Max 1000 characters'),
	nameForInvoice: Yup.string().max(255, 'Max 255 characters'),
	dashboardDescription: Yup.string().max(1000, 'Max 1000 characters'),
	value: Yup.number().required('Value is required').min(0, 'Must be >= 0'),
	importOrdersThreshold: Yup.number()
		.required('Import Orders Threshold is required')
		.integer('Must be an integer')
		.min(0, 'Must be >= 0'),
	ossOrdersThreshold: Yup.number()
		.required('OSS Orders Threshold is required')
		.integer('Must be an integer')
		.min(0, 'Must be >= 0'),
});

export const MonthlyFeeDialog: FC<TMonthlyFeeDialogProps> = ({
	open,
	onClose,
	monthlyFeeToEdit,
}) => {
	const dispatch = useDispatch();
	const isEdit = !!monthlyFeeToEdit;

	const loading = useSelector(getMonthlyFeeSaveLoading);
	const saveSuccess = useSelector(getMonthlyFeeSaveSuccess);
	const feeClassOptions = useSelector(getFeeClassOptions);

	useEffect(() => {
		if (!open) return;
		dispatch(getFeeClassOptionsRequest());
	}, [dispatch, open]);

	const formik = useFormik({
		initialValues: {
			name: monthlyFeeToEdit?.name ?? '',
			description: monthlyFeeToEdit?.description ?? '',
			nameForInvoice: monthlyFeeToEdit?.nameForInvoice ?? '',
			dashboardDescription: monthlyFeeToEdit?.dashboardDescription ?? '',
			value: monthlyFeeToEdit?.value ?? ('' as unknown as number),
			importOrdersThreshold: monthlyFeeToEdit?.importOrdersThreshold ?? ('' as unknown as number),
			ossOrdersThreshold: monthlyFeeToEdit?.ossOrdersThreshold ?? ('' as unknown as number),
			feeClassId: monthlyFeeToEdit?.feeClassId ?? null,
			showForAll: monthlyFeeToEdit?.showForAll ?? false,
			discontinued: monthlyFeeToEdit?.discontinued ?? false,
		},
		enableReinitialize: true,
		validationSchema,
		onSubmit: (values) => {
			const body = {
				name: values.name,
				description: values.description,
				nameForInvoice: values.nameForInvoice,
				dashboardDescription: values.dashboardDescription,
				value: Number(values.value),
				importOrdersThreshold: Number(values.importOrdersThreshold),
				ossOrdersThreshold: Number(values.ossOrdersThreshold),
				feeClassId: values.feeClassId,
				showForAll: values.showForAll,
				discontinued: values.discontinued,
			};
			if (isEdit && monthlyFeeToEdit) {
				dispatch(updateMonthlyFeeRequest({ id: monthlyFeeToEdit.id, body }));
			} else {
				dispatch(createMonthlyFeeRequest(body));
			}
		},
	});

	useEffect(() => {
		if (saveSuccess) {
			dispatch(resetSaveMonthlyFeeSuccess());
			formik.resetForm();
			onClose();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [saveSuccess]);

	const selectedFeeClass = useMemo(
		() => feeClassOptions?.find((option) => option.id === formik.values.feeClassId) ?? null,
		[feeClassOptions, formik.values.feeClassId],
	);

	const handleClose = () => {
		formik.resetForm();
		onClose();
	};

	return (
		<Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				{isEdit ? 'Edit Monthly Subscription' : 'Add New Monthly Subscription'}
			</DialogTitle>
			<DialogContent>
				<Box
					component="form"
					onSubmit={formik.handleSubmit}
					sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
				>
					<TextField
						label="Name"
						name="name"
						size="small"
						value={formik.values.name}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.name && !!formik.errors.name}
						helperText={formik.touched.name && formik.errors.name}
					/>
					<TextField
						label="Description"
						name="description"
						size="small"
						multiline
						minRows={2}
						value={formik.values.description}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.description && !!formik.errors.description}
						helperText={formik.touched.description && formik.errors.description}
					/>
					<TextField
						label="Name For Invoice"
						name="nameForInvoice"
						size="small"
						value={formik.values.nameForInvoice}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.nameForInvoice && !!formik.errors.nameForInvoice}
						helperText={formik.touched.nameForInvoice && formik.errors.nameForInvoice}
					/>
					<TextField
						label="Dashboard Description"
						name="dashboardDescription"
						size="small"
						multiline
						minRows={2}
						value={formik.values.dashboardDescription}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.dashboardDescription && !!formik.errors.dashboardDescription}
						helperText={formik.touched.dashboardDescription && formik.errors.dashboardDescription}
					/>
					<TextField
						label="Value, EUR"
						name="value"
						type="number"
						size="small"
						inputProps={{ step: '0.01' }}
						value={formik.values.value}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.value && !!formik.errors.value}
						helperText={formik.touched.value && formik.errors.value}
					/>
					<Autocomplete
						options={feeClassOptions ?? []}
						value={selectedFeeClass}
						getOptionLabel={(option) => option.description ?? String(option.id)}
						isOptionEqualToValue={(option, value) => option.id === value.id}
						onChange={(event, option) => formik.setFieldValue('feeClassId', option?.id ?? null)}
						renderInput={(params) => (
							<TextField {...params} label="Transaction Fee Class" size="small" />
						)}
					/>
					<TextField
						label="Import Orders Threshold"
						name="importOrdersThreshold"
						type="number"
						size="small"
						value={formik.values.importOrdersThreshold}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.importOrdersThreshold && !!formik.errors.importOrdersThreshold}
						helperText={formik.touched.importOrdersThreshold && formik.errors.importOrdersThreshold}
					/>
					<TextField
						label="OSS Orders Threshold"
						name="ossOrdersThreshold"
						type="number"
						size="small"
						value={formik.values.ossOrdersThreshold}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						error={formik.touched.ossOrdersThreshold && !!formik.errors.ossOrdersThreshold}
						helperText={formik.touched.ossOrdersThreshold && formik.errors.ossOrdersThreshold}
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="showForAll"
								checked={formik.values.showForAll}
								onChange={formik.handleChange}
							/>
						}
						label="Show For All"
					/>
					<FormControlLabel
						control={
							<Checkbox
								name="discontinued"
								checked={formik.values.discontinued}
								onChange={formik.handleChange}
							/>
						}
						label="Discontinued"
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose} disabled={loading}>
					Cancel
				</Button>
				<Button
					onClick={() => formik.handleSubmit()}
					variant="contained"
					color="primary"
					disabled={loading}
					startIcon={loading ? <CircularProgress size={16} /> : undefined}
				>
					{isEdit ? 'Save' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};
